import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Sparkles,
    ImageIcon,
    Save,
    Loader2,
    Trash2,
    Plus,
    X,
    ArrowLeft,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
    slug: "",
    name: "",
    tagline: "",
    hero_headline: "",
    hero_subheadline: "",
    short_description: "",
    long_description: "",
    category: "Hair Care",
    images: [],
    bundles: [
        { quantity: 1, label: "1 Bottle", price: 2499, original_price: 3999, bonus: "", is_popular: false },
    ],
    benefits: [],
    faqs: [],
    is_flagship: false,
    is_active: true,
};

export default function AdminProductForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [aiCopyLoading, setAiCopyLoading] = useState(false);
    const [aiImgLoading, setAiImgLoading] = useState(false);
    const [aiPrompt, setAiPrompt] = useState({ title: "", keywords: "" });
    const [imgPrompt, setImgPrompt] = useState("");

    useEffect(() => {
        if (!isEdit) return;
        setLoading(true);
        api.get("/admin/products")
            .then((r) => {
                const p = r.data.find((x) => x.id === id);
                if (p) setForm({ ...EMPTY, ...p });
            })
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const generateCopy = async () => {
        if (!aiPrompt.title.trim()) {
            toast.error("Enter a product title first");
            return;
        }
        setAiCopyLoading(true);
        try {
            const { data } = await api.post("/admin/ai/description", aiPrompt);
            update({
                name: form.name || aiPrompt.title,
                slug: form.slug || slugify(aiPrompt.title),
                tagline: data.tagline || "",
                hero_headline: data.hero_headline || "",
                hero_subheadline: data.hero_subheadline || "",
                short_description: data.short_description || "",
                long_description: data.long_description || "",
                benefits: Array.isArray(data.benefits) ? data.benefits : [],
                faqs: Array.isArray(data.faqs) ? data.faqs : [],
            });
            toast.success("AI copy generated");
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Failed. The LLM key may be out of balance — top up and retry.",
            );
        } finally {
            setAiCopyLoading(false);
        }
    };

    const generateImage = async () => {
        if (!imgPrompt.trim()) {
            toast.error("Describe the product image first");
            return;
        }
        setAiImgLoading(true);
        try {
            const { data } = await api.post("/admin/ai/image", {
                prompt: imgPrompt,
                slug: form.slug || slugify(form.name || "product"),
            });
            update({ images: [...(form.images || []), data.url] });
            toast.success("AI image added");
        } catch (err) {
            toast.error(
                formatApiError(err?.response?.data?.detail) ||
                    "Image generation failed. The LLM key may be out of balance.",
            );
        } finally {
            setAiImgLoading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEdit) {
                await api.put(`/admin/products/${id}`, form);
                toast.success("Product updated");
            } else {
                await api.post("/admin/products", form);
                toast.success("Product created");
            }
            navigate("/admin/products");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-product-form"
            className="space-y-6 max-w-5xl"
        >
            <Link
                to="/admin/products"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-forest-700 hover:text-gold-600"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to products
            </Link>

            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-800">
                        {isEdit ? "Edit product" : "Create product"}
                    </h1>
                    <p className="text-sm text-forest-900/70 mt-1">
                        Let Claude write the copy and Nano Banana paint the
                        product shots.
                    </p>
                </div>
            </header>

            {/* AI copy */}
            <section className="bg-forest-900 text-cream rounded-3xl p-6 sm:p-7 border border-gold-500/20 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-gold-500/10 blur-3xl pointer-events-none" />
                <div className="relative z-10">
                    <div className="flex items-center gap-2 text-gold-400 text-xs font-bold uppercase tracking-widest mb-3">
                        <Sparkles className="w-4 h-4" />
                        AI Copywriter · Claude Sonnet
                    </div>
                    <h2 className="font-serif text-2xl font-bold mb-4">
                        Describe your product, we'll write the page.
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                            data-testid="ai-title"
                            value={aiPrompt.title}
                            onChange={(e) =>
                                setAiPrompt((p) => ({ ...p, title: e.target.value }))
                            }
                            placeholder="e.g. Jojoba Facial Oil"
                            className="bg-forest-800 border border-cream/10 rounded-xl px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:ring-2 focus:ring-gold-500"
                        />
                        <input
                            data-testid="ai-keywords"
                            value={aiPrompt.keywords}
                            onChange={(e) =>
                                setAiPrompt((p) => ({ ...p, keywords: e.target.value }))
                            }
                            placeholder="keywords: anti-ageing, vitamin E, Pakistani skin"
                            className="bg-forest-800 border border-cream/10 rounded-xl px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:ring-2 focus:ring-gold-500"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={generateCopy}
                        disabled={aiCopyLoading}
                        data-testid="generate-copy-btn"
                        className="mt-4 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 disabled:opacity-70 text-forest-900 font-bold text-sm px-5 py-3 rounded-xl shadow"
                    >
                        {aiCopyLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Sparkles className="w-4 h-4" />
                        )}
                        Generate copy with AI
                    </button>
                </div>
            </section>

            <form onSubmit={save} className="space-y-6">
                <Section title="Basic details">
                    <TextInput
                        label="Name *"
                        value={form.name}
                        onChange={(v) =>
                            update({ name: v, slug: form.slug || slugify(v) })
                        }
                        testid="field-name"
                    />
                    <TextInput
                        label="Slug *"
                        value={form.slug}
                        onChange={(v) => update({ slug: v })}
                        testid="field-slug"
                        hint="Used in the public product URL."
                    />
                    <TextInput
                        label="Tagline"
                        value={form.tagline}
                        onChange={(v) => update({ tagline: v })}
                        testid="field-tagline"
                    />
                    <TextInput
                        label="Category"
                        value={form.category}
                        onChange={(v) => update({ category: v })}
                        testid="field-category"
                    />
                    <TextInput
                        label="Hero headline"
                        value={form.hero_headline}
                        onChange={(v) => update({ hero_headline: v })}
                        className="sm:col-span-2"
                        testid="field-hero-headline"
                    />
                    <TextArea
                        label="Hero subheadline"
                        value={form.hero_subheadline}
                        onChange={(v) => update({ hero_subheadline: v })}
                        className="sm:col-span-2"
                        testid="field-hero-subheadline"
                        rows={2}
                    />
                    <TextArea
                        label="Short description"
                        value={form.short_description}
                        onChange={(v) => update({ short_description: v })}
                        className="sm:col-span-2"
                        testid="field-short-description"
                        rows={2}
                    />
                    <TextArea
                        label="Long description"
                        value={form.long_description}
                        onChange={(v) => update({ long_description: v })}
                        className="sm:col-span-2"
                        testid="field-long-description"
                        rows={4}
                    />
                    <Toggle
                        label="Flagship product"
                        checked={form.is_flagship}
                        onChange={(v) => update({ is_flagship: v })}
                        testid="field-flagship"
                    />
                    <Toggle
                        label="Active (visible in shop)"
                        checked={form.is_active}
                        onChange={(v) => update({ is_active: v })}
                        testid="field-active"
                    />
                </Section>

                <Section title="Images">
                    <div className="sm:col-span-2 space-y-4">
                        <div className="bg-forest-50 border border-forest-100 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <div className="flex-1 w-full">
                                <label className="text-[11px] font-bold text-forest-800 uppercase tracking-widest block mb-1.5">
                                    AI image prompt
                                </label>
                                <input
                                    data-testid="ai-image-prompt"
                                    value={imgPrompt}
                                    onChange={(e) => setImgPrompt(e.target.value)}
                                    placeholder="e.g. amber glass dropper bottle of jojoba oil on marble"
                                    className="w-full bg-white border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={generateImage}
                                disabled={aiImgLoading}
                                data-testid="generate-image-btn"
                                className="shrink-0 inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-gold-400 font-bold text-sm px-5 py-2.5 rounded-xl border border-gold-500/40"
                            >
                                {aiImgLoading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <ImageIcon className="w-4 h-4" />
                                )}
                                Generate image
                            </button>
                        </div>

                        <TextInput
                            label="Add image URL manually"
                            value=""
                            placeholder="https://… and press Enter"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && e.currentTarget.value.trim()) {
                                    e.preventDefault();
                                    update({ images: [...(form.images || []), e.currentTarget.value.trim()] });
                                    e.currentTarget.value = "";
                                }
                            }}
                            testid="field-image-url"
                        />

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {form.images.map((src, i) => (
                                <div
                                    key={i}
                                    className="relative aspect-square rounded-xl overflow-hidden border border-forest-100 bg-forest-50 group"
                                >
                                    <img
                                        src={src}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            update({
                                                images: form.images.filter((_, k) => k !== i),
                                            })
                                        }
                                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                <Section title="Bundles">
                    <div className="sm:col-span-2 space-y-3">
                        {form.bundles.map((b, i) => (
                            <div
                                key={i}
                                className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end bg-cream rounded-xl p-3 border border-forest-100"
                            >
                                <TextInput
                                    label="Qty"
                                    type="number"
                                    value={b.quantity}
                                    onChange={(v) =>
                                        update({
                                            bundles: form.bundles.map((x, k) =>
                                                k === i ? { ...x, quantity: Number(v) || 1 } : x,
                                            ),
                                        })
                                    }
                                />
                                <TextInput
                                    className="col-span-2"
                                    label="Label"
                                    value={b.label}
                                    onChange={(v) =>
                                        update({
                                            bundles: form.bundles.map((x, k) =>
                                                k === i ? { ...x, label: v } : x,
                                            ),
                                        })
                                    }
                                />
                                <TextInput
                                    label="Price (PKR)"
                                    type="number"
                                    value={b.price}
                                    onChange={(v) =>
                                        update({
                                            bundles: form.bundles.map((x, k) =>
                                                k === i ? { ...x, price: Number(v) || 0 } : x,
                                            ),
                                        })
                                    }
                                />
                                <TextInput
                                    label="Was (PKR)"
                                    type="number"
                                    value={b.original_price}
                                    onChange={(v) =>
                                        update({
                                            bundles: form.bundles.map((x, k) =>
                                                k === i ? { ...x, original_price: Number(v) || 0 } : x,
                                            ),
                                        })
                                    }
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        update({
                                            bundles: form.bundles.filter((_, k) => k !== i),
                                        })
                                    }
                                    className="h-10 bg-rose-50 text-rose-700 rounded-xl flex items-center justify-center"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <TextInput
                                    className="col-span-2 sm:col-span-6"
                                    label="Bonus / badge (optional)"
                                    value={b.bonus || ""}
                                    onChange={(v) =>
                                        update({
                                            bundles: form.bundles.map((x, k) =>
                                                k === i ? { ...x, bonus: v } : x,
                                            ),
                                        })
                                    }
                                />
                                <Toggle
                                    className="col-span-2 sm:col-span-6"
                                    label="Mark as most popular"
                                    checked={!!b.is_popular}
                                    onChange={(v) =>
                                        update({
                                            bundles: form.bundles.map((x, k) =>
                                                k === i ? { ...x, is_popular: v } : x,
                                            ),
                                        })
                                    }
                                />
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={() =>
                                update({
                                    bundles: [
                                        ...form.bundles,
                                        { quantity: form.bundles.length + 1, label: "", price: 0, original_price: 0 },
                                    ],
                                })
                            }
                            className="inline-flex items-center gap-2 text-xs font-bold text-forest-700 border border-forest-200 rounded-xl px-3 py-2 hover:bg-forest-50"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add bundle
                        </button>
                    </div>
                </Section>

                <Section title="Benefits">
                    <ListEditor
                        rows={form.benefits}
                        onChange={(benefits) => update({ benefits })}
                        fields={[
                            { key: "title", label: "Title", width: "sm:col-span-2" },
                            { key: "text", label: "Short explanation", width: "sm:col-span-3" },
                            { key: "icon", label: "Icon key", width: "sm:col-span-1", hint: "sparkles|leaf|droplets|dna|feather|sprout|shield|star" },
                        ]}
                        addLabel="Add benefit"
                        emptyRow={{ icon: "sparkles", title: "", text: "" }}
                    />
                </Section>

                <Section title="FAQs">
                    <ListEditor
                        rows={form.faqs}
                        onChange={(faqs) => update({ faqs })}
                        fields={[
                            { key: "q", label: "Question", width: "sm:col-span-2" },
                            { key: "a", label: "Answer", width: "sm:col-span-4", textarea: true },
                        ]}
                        addLabel="Add FAQ"
                        emptyRow={{ q: "", a: "" }}
                    />
                </Section>

                <div className="flex items-center gap-3">
                    <button
                        type="submit"
                        data-testid="save-product-btn"
                        disabled={loading}
                        className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-900 disabled:opacity-70 text-gold-400 font-bold text-sm px-6 py-3 rounded-xl border border-gold-500/40"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {isEdit ? "Save changes" : "Create product"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function slugify(name) {
    return (name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function Section({ title, children }) {
    return (
        <section className="bg-white rounded-2xl border border-forest-100 shadow-sm p-5 sm:p-6">
            <h3 className="font-serif text-lg font-bold text-forest-800 mb-4">
                {title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
        </section>
    );
}

function TextInput({
    label,
    value,
    onChange,
    className = "",
    hint,
    testid,
    ...props
}) {
    return (
        <div className={className}>
            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                {label}
            </label>
            <input
                data-testid={testid}
                value={value}
                onChange={(e) => onChange && onChange(e.target.value)}
                className="w-full bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:bg-white"
                {...props}
            />
            {hint && (
                <span className="text-[11px] text-forest-700/70 mt-1 block">
                    {hint}
                </span>
            )}
        </div>
    );
}

function TextArea({ label, value, onChange, className = "", testid, rows = 3 }) {
    return (
        <div className={className}>
            <label className="block text-[11px] font-bold text-forest-800 uppercase tracking-widest mb-1.5">
                {label}
            </label>
            <textarea
                data-testid={testid}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                rows={rows}
                className="w-full bg-forest-50/50 border border-forest-100 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:bg-white"
            />
        </div>
    );
}

function Toggle({ label, checked, onChange, className = "", testid }) {
    return (
        <label
            className={`flex items-center gap-3 bg-forest-50/60 border border-forest-100 rounded-xl px-4 py-3 cursor-pointer ${className}`}
        >
            <input
                data-testid={testid}
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 accent-forest-700"
            />
            <span className="text-xs font-bold text-forest-800 uppercase tracking-widest">
                {label}
            </span>
        </label>
    );
}

function ListEditor({ rows, onChange, fields, addLabel, emptyRow }) {
    return (
        <div className="sm:col-span-2 space-y-3">
            {rows.map((row, i) => (
                <div
                    key={i}
                    className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end bg-cream rounded-xl p-3 border border-forest-100"
                >
                    {fields.map((f) =>
                        f.textarea ? (
                            <TextArea
                                key={f.key}
                                className={f.width}
                                label={f.label}
                                value={row[f.key]}
                                onChange={(v) =>
                                    onChange(
                                        rows.map((r, k) =>
                                            k === i ? { ...r, [f.key]: v } : r,
                                        ),
                                    )
                                }
                                rows={2}
                            />
                        ) : (
                            <TextInput
                                key={f.key}
                                className={f.width}
                                label={f.label}
                                hint={f.hint}
                                value={row[f.key] ?? ""}
                                onChange={(v) =>
                                    onChange(
                                        rows.map((r, k) =>
                                            k === i ? { ...r, [f.key]: v } : r,
                                        ),
                                    )
                                }
                            />
                        ),
                    )}
                    <button
                        type="button"
                        onClick={() =>
                            onChange(rows.filter((_, k) => k !== i))
                        }
                        className="h-10 bg-rose-50 text-rose-700 rounded-xl flex items-center justify-center"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={() => onChange([...rows, { ...emptyRow }])}
                className="inline-flex items-center gap-2 text-xs font-bold text-forest-700 border border-forest-200 rounded-xl px-3 py-2 hover:bg-forest-50"
            >
                <Plus className="w-3.5 h-3.5" /> {addLabel}
            </button>
        </div>
    );
}
